import Log from 'utils/Log'
import {
  path,
  propOr,
  head,
  propSatisfies,
  length,
  pathEq,
  omit,
  where,
  prop,
  equals,
  complement,
  propEq,
  isEmpty,
  includes,
  pathOr,
  has,
} from 'ramda'
import { displayModuleName } from 'i18n/displayConstants'
import {
  O_MAP,
  AS_BUILT,
  NONEXISTANT,
  AVAILABLE,
  ASSIGNED,
  RESTRICTED,
  FIXED,
  INPUT,
  OUTPUT,
  MODULE,
  M2_MODULES,
  CHECPLUS_MODULE_TYPES,
  CHECPLUS_MODULE_NAMES,
  MOVABLE,
  SHM,
  EXM_BASE,
  EXM_SIZE,
  DIGITAL,
  ANALOG,
  UNAVAILABLE,
  VEHICLE_SUBTYPES,
  restrictedModules,
  BHM_C7C_PIN,
} from 'utils/constants'
import { MODULE_NAME_INTERFACE } from 'constants/ecus'
import strings from './strings'
import { zeroPad, isEmptyString } from './utils'

const getModuleSetBySubType = (vehicleSubType) => {
  if (vehicleSubType === VEHICLE_SUBTYPES.BUS) {
    return new Set([
      M2_MODULES.BHM,
      M2_MODULES.CHM,
      M2_MODULES.EXM1,
      M2_MODULES.EXM2,
      M2_MODULES.EXM3,
      M2_MODULES.SHM,
    ])
  }
  if (vehicleSubType === VEHICLE_SUBTYPES.RV) {
    return new Set([M2_MODULES.DASH_O, M2_MODULES.CHM])
  }
  return new Set([
    M2_MODULES.BHM,
    M2_MODULES.CHM,
    M2_MODULES.EXM1,
    M2_MODULES.EXM2,
    M2_MODULES.EXM3,
  ])
}

const excludeConnector = (module, connector) => {
  if (module === M2_MODULES.BHM && connector === 'C7C') {
    return true
  }
  if (module === M2_MODULES && !new Set(['J1', 'J2', 'J3']).has(connector)) {
    return true
  }
  return false
}

const PIN_CONFLICTS = {
  SIBLING: strings.common_node_conflict,
  PENDING: strings.pending,
}

const hasSiblingConflict = (siblings, currAssignments, circuits) => {
  const hasConflictWithCurrAssignment = siblings.some(({ uri }) => {
    const [, module, connector, pin] = uri.split('/')
    const hasSameValues = currAssignments.some(
      (assignment) =>
        prop('module', assignment) === module &&
        prop('connector', assignment) === connector &&
        path(['pin', 'location'], assignment) === pin &&
        circuits.some(
          (circuit) => circuit.circuitNumber === assignment.circuitNumber
        )
    )

    return hasSameValues
  })

  return hasConflictWithCurrAssignment
}

export const getVehiclePinsForCircuit = (
  circuit,
  vehicleCtpDictionary,
  currAssignments = [],
  vehicleSubType,
  circuits = []
) => {
  const moduleSet = getModuleSetBySubType(vehicleSubType)
  const inputCircuit = circuitIsInput(circuit)
  const isPinOfCorrectType = propSatisfies(
    inputCircuit ? typeIsInput : typeIsOutput,
    'pinType'
  )

  const vehicleCtpDictionaryWithoutModules = omit(
    ['modules'],
    vehicleCtpDictionary
  )
  const pinObjects = Object.values(vehicleCtpDictionaryWithoutModules)
    .reduce((accum, entity) => {
      if (!prop('uri', entity)) return accum

      const [, module, connector, pin] = entity.uri.split('/')

      if (
        !isPinOfCorrectType(entity) ||
        !moduleSet.has(module) ||
        excludeConnector(module, connector)
      ) {
        return [...accum]
      }

      if (
        length(currAssignments) &&
        currAssignments.some(
          where({
            module: equals(module),
            connector: equals(connector),
            pin: pathEq(['location'], pin),
            circuitNumber: complement(equals(prop('circuitNumber', entity))),
          })
        )
      ) {
        return [
          ...accum,
          {
            ...entity,
            mappingStatus: PIN_CONFLICTS.PENDING,
          },
        ]
      }

      const commonNodeSiblings = propOr(false, 'commonNodeSiblings', entity)
      const isSiblingConflict =
        commonNodeSiblings &&
        length(currAssignments) &&
        hasSiblingConflict(commonNodeSiblings, currAssignments, circuits)

      if (isSiblingConflict) {
        return [
          ...accum,
          {
            ...entity,
            mappingStatus: PIN_CONFLICTS.SIBLING,
          },
        ]
      }

      if (entity.circuitNumber) {
        return [
          ...accum,
          {
            ...entity,
            mappingStatus: entity.circuitNumber,
          },
        ]
      }

      return [
        ...accum,
        {
          ...entity,
          mappingStatus: AVAILABLE,
        },
      ]
    }, [])
    .map((pin) => {
      const [, moduleName, connectorName] = pin.uri.split('/')
      return {
        ...pin,
        moduleName,
        connectorName,
        key: `${pin.location}-${connectorName}-${moduleName}`,
      }
    })
  return pinObjects
}

export const filterPinsForCircuit =
  (circuit, currentAssignments = [], connectorName, moduleName, connectors) =>
  (pin) => {
    // pin must have a type
    const pinHasNoPinType = !pin.pinType
    if (pinHasNoPinType) {
      return false
    }
    // pin must be of the same type as the circuit
    const pinIsSameTypeAsCircuit = circuitIsInput(circuit)
      ? pinIsInput(pin)
      : pinIsOutput(pin)
    if (!pinIsSameTypeAsCircuit) {
      return false
    }
    // ... and not assigned to another circuit
    const pinIsAvailableForAssignment =
      pin.circuitNumber === circuit.circuitNumber || !pin.circuitNumber // do we still need to include the pin to which the circuit is assigned?
    if (!pinIsAvailableForAssignment) {
      return false
    }
    // ... and not a currently assigned pin
    const pinIsNotInCurrentAssignmentsQueue = !currentAssignments.find(
      (assignment) =>
        assignment.connector === connectorName &&
        assignment.pin === pin.location
    )
    if (!pinIsNotInCurrentAssignmentsQueue) {
      return false
    }

    // ... and a common node sibling is not occupied
    const pinCommonNodeSiblingsNotMapped = commonNodeSiblingsNotMapped(
      pin,
      moduleName,
      connectors,
      currentAssignments
    )
    if (!pinCommonNodeSiblingsNotMapped) {
      return false
    }

    return true
  }

export const commonNodeSiblingsNotMapped = (
  pin,
  moduleName,
  connectors,
  currentAssignments
) => {
  if (!pin.commonNodeSiblings || !pin.commonNodeSiblings.length) return true
  const siblingsNotMapped = pin.commonNodeSiblings.every((sib) => {
    const [conn, pinNum] = sib.split('-')
    const sibsConn = connectors.find(
      (c) =>
        parseCNSConnectorStringForDisplay(c.connectorName, moduleName) === conn
    )

    const siblingPinObj = sibsConn.pins.find((p) => p.location === pinNum)
    const siblingDoesNotHaveExistingCircuitAssignment =
      !siblingPinObj.circuitNumber
    const siblingIsNotPartOfPendingAssignment = !currentAssignments.find(
      pinIsInCurrentAssignmentsArray(siblingPinObj, conn, true)
    )
    return (
      siblingIsNotPartOfPendingAssignment &&
      siblingDoesNotHaveExistingCircuitAssignment
    )
  })
  return siblingsNotMapped
}

export const pinIsInCurrentAssignmentsArray =
  (pin, connectorName, cns) => (assignment) =>
    (pin.location || pin.pin) === assignment.pin && cns
      ? parseCNSConnectorStringForDisplay(
          assignment.connector,
          assignment.module
        ) === connectorName
      : assignment.connector === connectorName

export const filterModulesForCircuit = (circuit) => (module) => {
  if (module.moduleName === SHM) return false
  return circuitIsInput(circuit)
    ? module.moduleName === circuitModule(circuit)
    : true
}

// Handles two variations on pin array element structure.
const subPinObjects = (pins) =>
  pins && pins.length > 0 && pins[0] && pins[0].pin

const getPin = (pin, sub) => (sub ? pin.pin : pin)

const getConnectedInterface = (
  newPin,
  interfaceModule,
  { connectorName, moduleName }
) => {
  const { location } = newPin
  let interfacePin
  interfaceModule.connectors
    .filter((con) => con.ecu === moduleName)
    .forEach((con) => {
      const interfacePinData = con.pins.find(
        (pin) =>
          pin.ecuConnector === connectorName && pin.ecuPinLocation === location
      )
      if (interfacePinData) {
        interfacePin = {
          ...interfacePinData,
          connectorName: con.connectorName,
        }
      }
    })

  return interfacePin
}

export const normalizePins = (pins, moduleName, options = {}) => {
  const normalizedPins = pins.slice(0)
  const sub = subPinObjects(pins)
  const isInterface = moduleName === MODULE_NAME_INTERFACE
  // CEEA+ temporary, pending API consistency refactor.
  if (isInterface || has(moduleName, CHECPLUS_MODULE_NAMES)) {
    const { interfaceModule, ecuData } = options
    normalizedPins.forEach((pin, idx) => {
      const realPin = getPin(pin, sub)

      if (interfaceModule && ecuData) {
        const interfaceData = getConnectedInterface(
          realPin,
          interfaceModule,
          ecuData
        )
        if (interfaceData) {
          realPin.interfaceData = interfaceData
        }
      }
      normalizedPins[idx] = sub ? { ...pin, pin: realPin } : realPin
    })
  }
  // Already ordered right for M3/ASAM, but not for XMC.
  // Sort all to be sure.
  return normalizedPins.sort((a, b) =>
    getPinOrdinal(getPin(a, sub).pinNumber) <
    getPinOrdinal(getPin(b, sub).pinNumber)
      ? -1
      : 0
  )
}

export const mapPinForCircuitTargetsTable = (pin) => {
  const [, modName, conName] = pin.uri.split('/')
  return {
    key: `${pin.location}-${conName}-${modName}`,
    location: pin.location,
    pinType: pin.pinType,
    isRedacted: pin.isRedacted,
    connectorName: conName,
    moduleName: modName,
    circuitNumber: pin.circuitNumber,
    currentLimit: pin.currentLimit,
    commonNodeSiblings: pin.commonNodeSiblings,
  }
}
export const pinIsViableTargetForCircuit =
  (sourcePinType) =>
  ({ circuitNumber, pinType }) =>
    isEmptyString(circuitNumber) && pinsAreOfSameType(sourcePinType, pinType)

export const pinsAreOfSameType = (sourcePinType, potentialTargetType) => {
  if (!potentialTargetType) return false
  const upperCaseSource = sourcePinType.toUpperCase()
  const upperCaseTarget = potentialTargetType.toUpperCase()

  if (upperCaseSource.includes(DIGITAL) && upperCaseTarget.includes(DIGITAL)) {
    if (upperCaseSource.includes(OUTPUT) && upperCaseTarget.includes(OUTPUT)) {
      return true
    }
    if (upperCaseSource.includes(INPUT) && upperCaseTarget.includes(INPUT)) {
      return true
    }
    return false
  }
  if (upperCaseSource.includes(ANALOG) && upperCaseTarget.includes(ANALOG)) {
    if (upperCaseSource.includes(OUTPUT) && upperCaseTarget.includes(OUTPUT)) {
      return true
    }
    if (upperCaseSource.includes(INPUT) && upperCaseTarget.includes(INPUT)) {
      return true
    }
    return false
  }
  return false
}

export const getAvailablePinsOfLikeType = (sourcePin, ctpDictionary) => {
  const viablePins = Object.values(ctpDictionary).filter(
    pinIsViableTargetForCircuit(sourcePin.pinType)
  )

  const pinDestination = whereCanPinBeMoved(sourcePin.pinType)
  const [, moduleName] = sourcePin.uri.split('/')
  if (pinDestination === MODULE) {
    return viablePins
      .filter(({ uri }) => uri.includes(moduleName))
      .map(mapPinForCircuitTargetsTable)
  }

  // if pin is from BHM/C7C or from SHM then filter it
  if (pinDestination === MOVABLE) {
    return viablePins
      .filter(({ uri }) => !uri.includes(SHM) && !uri.includes(BHM_C7C_PIN)) // TODO remove this when the API no longer returns data for this module
      .map(mapPinForCircuitTargetsTable)
  }

  return null
}

export const whereCanPinBeMoved = (pinType) => {
  if (!pinType) return FIXED
  if (pinType.toUpperCase().includes(INPUT)) return MODULE
  if (pinType.toUpperCase().includes(OUTPUT)) return MOVABLE
  return FIXED
}

export const sortPinsForCircuit = (pinArray) =>
  pinArray
    .sort((a, b) => (a.pin > b.pin ? 1 : -1))
    .sort((a, b) => (a.connector < b.connector ? 1 : -1))
    .sort((a, b) => (a.module > b.module ? 1 : -1))

const getAvailablePinsForCircuit = (
  circuit,
  vehicleCtpDictionary,
  currAssignments = [],
  vehicleSubType,
  circuits = []
) => {
  const pins = getVehiclePinsForCircuit(
    circuit,
    vehicleCtpDictionary,
    (currAssignments = []),
    vehicleSubType,
    (circuits = [])
  ).filter((pin) => pin.mappingStatus === AVAILABLE)

  return pins
}

const getPinsAvailabilityForCircuits = (
  circuits = [],
  vehicleCtpDictionary = {},
  vehicleSubType
) => {
  const pinsAvailable = getAvailablePinsForCircuit(
    head(circuits),
    vehicleCtpDictionary,
    [],
    vehicleSubType
  )
  const pinsAvailableForCircuits = circuits.length <= pinsAvailable.length
  return pinsAvailableForCircuits
}

// Once we determine that an RP requires pin assignment, we then need to know if there are any available pins - input or output based on circuit type
// input pins on the vehicle receive that assignment on the module decided by the inverted value in the input circuit
// output pins on vehicle recieve assignment based on output circuits
export const pinsAreAvailableForAllCircuits = (
  circuitsThatRequireMapping = [],
  vehicleCtpDictionary = {},
  vehicleSubType
) => {
  const inputCircuits = circuitsThatRequireMapping.filter((circuit) =>
    circuitIsInput(circuit)
  )
  const outputCircuits = circuitsThatRequireMapping.filter(
    (circuit) => !circuitIsInput(circuit)
  )

  // note, returning true on empty allows whichever list that actually has circuits determine validity
  const isInputCircuitToPinMappingValid = isEmpty(inputCircuits)
    ? true
    : getPinsAvailabilityForCircuits(
        inputCircuits,
        vehicleCtpDictionary,
        vehicleSubType
      )
  const isOutputCircuitToPinMappingValid = isEmpty(outputCircuits)
    ? true
    : getPinsAvailabilityForCircuits(
        outputCircuits,
        vehicleCtpDictionary,
        vehicleSubType
      )
  return isInputCircuitToPinMappingValid && isOutputCircuitToPinMappingValid
}

// Determine circuit tye - input/output, module type for circuit mapping, pin type - input/output
export const circuitDirection = (circuit) =>
  circuitIsInput(circuit) ? 'input' : 'output'
export const circuitIsInput = (circuit) => circuit.priority === '65535'
export const circuitModule = (circuit) =>
  circuit.inverted === '0' ? M2_MODULES.BHM : M2_MODULES.CHM
export const pinIsInput = (pin) => pin.pinType.toUpperCase().includes(INPUT)
export const pinIsOutput = (pin) => pin.pinType.toUpperCase().includes(OUTPUT)
export const typeIsInput = (type) => type && type.toUpperCase().includes(INPUT)
export const typeIsOutput = (type) =>
  type && type.toUpperCase().includes(OUTPUT)

export const getDefaultConnector = (moduleName, modules) => {
  // CEEA+ module connectors are fixed
  if (moduleName === CHECPLUS_MODULE_TYPES.ASAM) return 'X7'
  if (moduleName === CHECPLUS_MODULE_TYPES.XMC) return 'X1'

  const module = modules.find((mod) => mod.moduleName === moduleName)

  if (!module) {
    Log.warn(`unexpected module name ${moduleName} in getDefaultConnector()`)
  }

  if (moduleName === MODULE_NAME_INTERFACE) {
    return module && module.connectors.map((con) => con.connectorName).sort()[0]
  }

  // M2 module connector names are quirky, default to lowest number.
  const conns = module.connectors.sort((a, b) =>
    a.connectorName > b.connectorName ? 1 : -1
  )
  return conns[0].connectorName
}

// Default pin is now always the first available in the sorted pin array.
export const getDefaultPinNumber = (pins) => {
  if (!pins || pins.length <= 0) return null
  return getPin(pins[0], subPinObjects(pins)).location
}

export const parseConnector = (connectorString) => {
  const splitString = connectorString.split('_')
  return splitString[splitString.length - 1]
}

export const pinIsFixed = (pin) => {
  if ([undefined, null, ''].includes(pin.pinType)) return true
  const type = pin.pinType.toUpperCase()
  if (pin.circuitNumber === 'N/A') return true
  if (!type.includes(INPUT) && !type.includes(OUTPUT)) return true

  return false
}

// Defines sort ordering of pins: by number if ID ccntains a number,
// else alphabetically.
export const getPinOrdinal = (pinId) => {
  // Handle A1...A11..B1..B11, "1", "A"..."Z".  See connectorPinMap.js
  const numStr = pinId.replace(/[^0-9]+/, '')
  const alpha = pinId.replace(/[0-9]+/, '')
  return alpha + zeroPad(numStr, 4) // 2 currently would be enough
}

export const getConnectorOrdinal = getPinOrdinal

export const getPrimaryModuleName = (str) => {
  // can str ever be CHM? others? Returning null here is very bad.
  // XXX Crowbar to CHEC+, until it returns a primaryModule.
  // Should derive ground truth on complete primary module list, log
  // an error if str is a non-primary value.
  if (!str) return CHECPLUS_MODULE_TYPES.ASAM
  if (str.includes(M2_MODULES.BHM)) return M2_MODULES.BHM
  if (str.includes(M2_MODULES.DASH_O) || str.includes('RVM'))
    return M2_MODULES.DASH_O
  return CHECPLUS_MODULE_TYPES.ASAM
}

// we could just pass the circuitNumber in as the 2nd argument, but it is important
// to remember the relationship between the selectedPin & the circuit we're
// interrogating
export const checkPinStatusByCircuitNumber = (
  asBuiltCircuits,
  selectedPin,
  oMapCircuitSet
) => {
  // Comment this line out to test movable circuits with mock data.
  const hasPinInAsBuilt = asBuiltCircuits.some(
    propEq('circuitNumber', selectedPin.circuitNumber)
  )
  if (hasPinInAsBuilt) return AS_BUILT
  if (oMapCircuitSet.has(selectedPin.circuitNumber)) return O_MAP
  return ASSIGNED
}

const getPinId = (pin) => pin.location

// /XMC3/X1 or /interface/INT1 or /ASAM/X7
// return XMC3 or interface or ASAM
const getModuleFromConnectorUri = (str) => pathOr('', [1], str.split('/'))

export const determineInitialPinEditStatus = (pin, connector, isChecPlus) => {
  // isChecPlus needs to include interface connectors
  const siblingPinIdSet = new Set(
    connector.pins.map((sibPin) => getPinId(sibPin))
  )

  const pinId = getPinId(pin)
  if (!pinId || !siblingPinIdSet.has(pinId)) {
    return NONEXISTANT
  }
  if (isChecPlus) {
    const module = getModuleFromConnectorUri(propOr('', 'uri', connector))
    const isRestricted = includes(module, restrictedModules)

    if (isRestricted) return RESTRICTED

    return pin.isMapped ? ASSIGNED : AVAILABLE
  }

  if (pinIsFixed(pin)) {
    return FIXED
  }
  return undefined
}

export const determinePinEditStatus = (asBuiltCircuits, pin, oMapCircuits) => {
  if ((pin.circuitNumber || '').toUpperCase() === UNAVAILABLE) {
    return UNAVAILABLE
  }
  if (!pin.circuitNumber) {
    return AVAILABLE
  }

  // In the case of chec+, there is no circuitNumber, so this will always return
  // AVAIABLE. This seems to have no impact (i.e. it's not relevant):
  return pin.circuitNumber
    ? checkPinStatusByCircuitNumber(asBuiltCircuits, pin, oMapCircuits)
    : null // this _should_ never happen
}

const exmSizesDisplayStrings = {
  [EXM_SIZE.SMALL]: strings.small,
  [EXM_SIZE.MEDIUM]: strings.medium,
  [EXM_SIZE.LARGE]: strings.large,
}

const exmSizesDisplayWrap = (x) => `(${x})`

export const determineEXMDisplayName = (module) => {
  const { moduleName, size } = module

  if (!moduleName || !moduleName.includes(EXM_BASE)) return moduleName
  const expandedName = `${moduleName.substring(0, 3)} ${moduleName.substring(
    3
  )}`

  const sizeName = exmSizesDisplayWrap(exmSizesDisplayStrings[size])

  return `${expandedName} ${sizeName}`
}

export const generateEcuListItem = (module) => ({
  value: module.moduleName,
  text: displayModuleName(module.moduleName) || determineEXMDisplayName(module),
  EXMSize: module.size,
})

export const getMinNewExmSize = (ecusOnVehicle) => {
  const existingEXM = ecusOnVehicle.find((ecu) =>
    ecu.moduleName.includes(EXM_BASE)
  )
  const existingEXMSize = existingEXM ? existingEXM.size : null
  const minUpgrade = {
    [EXM_SIZE.SMALL]: EXM_SIZE.MEDIUM,
    [EXM_SIZE.MEDIUM]: EXM_SIZE.LARGE,
    [EXM_SIZE.LARGE]: null,
  }
  return !existingEXMSize ? EXM_SIZE.SMALL : minUpgrade[existingEXMSize]
}

export const exmActionIsAdd = (minNewEXMSize) =>
  minNewEXMSize === EXM_SIZE.SMALL

export const parseCNSConnectorStringForDisplay = (connName, moduleName) => {
  const shortConnName = connName.substring(0, 2)
  return moduleName === M2_MODULES.BHM
    ? `B${shortConnName.match(/[1-9]/g)[0]}`
    : shortConnName
}

export const isPinUnavailableBecauseSiblings = (commonNodeSiblingsArr, pins) =>
  commonNodeSiblingsArr.reduce((acc, sibling) => {
    if (acc) {
      return acc
    }
    const pin = pins[sibling.uri]
    return prop('circuitNumber', pin) ? strings.unavailable : ''
  }, '')

/**
  @param string : "/vehicles/LL7074/BHM/C1C/M"
  @return array : ['BFM', 'C1C', 'M']
 */
export const splitLocationFromCircuitUri = (location) =>
  location.split('/').slice(3, 6)

export const getVehicleTypeViaUri = (uri) => {
  const checPlusModuleNames = `${Object.values(CHECPLUS_MODULE_NAMES).join(
    '|'
  )}|${MODULE_NAME_INTERFACE}`
  const matcher = uri.match(new RegExp(`^/(${checPlusModuleNames}).?`))
  return {
    isChecPlus: !!matcher,
    isInterfaceConnector: matcher && matcher[1] === MODULE_NAME_INTERFACE,
  }
}

const labelParser = (name) => {
  const matcher = name.match(/\/(INT|EXT|XMC)(\d)\/(X1|X2|X3)?.+/)
  if (matcher) {
    const [, conType, conNum, con] = matcher
    let label = `${conType} ${conNum}`
    label += con ? `-${con}` : ''
    return label
  }

  const asamMatch = name.match(/^\/ASAM/)
  if (asamMatch) {
    const [, ecu, connection] = name.split('/')
    return [ecu, connection].join('-')
  }

  return name
}

const buildPins = (pins) => {
  if (!pins) return {}

  const normalizedPins = Object.entries(pins).reduce((accum, [key, value]) => {
    const { isInterfaceConnector } = getVehicleTypeViaUri(key)
    const sourceUri = value.sourceData && value.sourceData.uri
    const sourcePin = sourceUri && pins[sourceUri]
    const { commonNodeSiblings = [] } = value

    const newPin = {
      ...value,
      ...(isInterfaceConnector && {
        ...(sourceUri &&
          sourcePin && {
            sourceData: {
              label: `${labelParser(sourceUri)}-P${sourcePin.location}`, // 'XMC 3-X1-P2'
              uri: sourceUri,
            },
            ...(sourcePin.isPinVpdmOutput && {
              isPinVpdmOutput: true,
              ...(parseFloat(sourcePin.currentLimit) < 20 && {
                convertedCurrentLimit: '20.00',
              }),
            }),
          }),
      }),
      ...(commonNodeSiblings.length > 0 && {
        circuitNumber:
          value.circuitNumber ||
          isPinUnavailableBecauseSiblings(commonNodeSiblings, pins),
      }),
    }
    const interfaceData = sourceUri && {
      label: `${labelParser(key)}-P${newPin.location}`, // 'EXT 1-P2'
      uri: key,
    }

    return {
      ...accum,
      [key]: {
        ...accum[key],
        ...newPin,
      },
      ...(sourceUri &&
        accum[sourceUri] && {
          [sourceUri]: {
            ...accum[sourceUri],
            interfaceData,
          },
        }),
    }
  }, {})

  return normalizedPins
}

const getPinLocation = (pin) => pin.split('/').pop()
const numSorting = (numA, numB) => (parseInt(numA) > parseInt(numB) ? 1 : -1)
const charSorting = (charA, charB) => charA - charB

const sortPins = (pins) =>
  pins.sort((a, b) => {
    const pinA = getPinLocation(a)
    const pinB = getPinLocation(b)

    const isNumber = !isNaN(parseInt(pinA)) // eslint-disable-line no-restricted-globals
    if (isNumber) {
      return numSorting(pinA, pinB)
    }
    const regEx = /^([a-zA-Z]*)(\d*)$/
    const [, charA, numA] = pinA.match(regEx)
    const [, charB, numB] = pinB.match(regEx)

    if (numA) {
      return charA === charB
        ? numSorting(numA, numB)
        : charSorting(charA, charB)
    }

    return charSorting(a, b)
  })

const buildConnectors = (connectors) => {
  if (!connectors) return {}

  const connectorsWithSortedPins = Object.entries(connectors).reduce(
    (accum, [key, { pins, ...restConnector }]) => ({
      ...accum,
      [key]: {
        ...restConnector,
        pins: pins && sortPins(pins),
      },
    }),
    {}
  )

  return connectorsWithSortedPins
}

const buildModules = (modules) => {
  if (!modules) return {}

  const modulesWithSortedConnectors = Object.entries(modules).reduce(
    (accum, [key, { connectors, ...restModule }]) => ({
      ...accum,
      [key]: {
        ...restModule,
        connectors: connectors.sort(),
      },
    }),
    {}
  )

  return modulesWithSortedConnectors
}

export const buildVehicleModules = ({ modules, connectors, pins }) => ({
  ...buildModules(modules),
  ...buildConnectors(connectors),
  ...buildPins(pins),
})

export const noAvailablePins = ({ dictionary, connector }) =>
  !connector.pins.find((pinUri) => !dictionary[pinUri].isMapped)

export const checkPinStatusByCircuitNumberWithLocation = (
  selectedPin,
  asBuiltCircuits
) => {
  if (!selectedPin) return ''

  if ([AS_BUILT, ASSIGNED].includes(selectedPin.status)) {
    const hasPinInAsBuilt = asBuiltCircuits.some(
      (x) =>
        x.circuitNumber === selectedPin.circuitNumber &&
        x.pin === selectedPin.location
    )
    if (hasPinInAsBuilt) return AS_BUILT
    return ASSIGNED
  }
  return selectedPin.status
}

export const makePinUriFromCircuit = (circuit) =>
  `/${circuit.module}/${circuit.connector}/${circuit.pin}`
